import React, { useMemo } from 'react';
import { EFormBarChart, EFormLineChart } from '../../e-forms/ReportCharts';
import { useEFormSubmissions } from '../../e-forms/useData';
import { WidgetShell } from './shared';

function parseDefinitionId(...candidates: unknown[]): number | null {
  for (const raw of candidates) {
    if (typeof raw === 'number' && Number.isFinite(raw) && raw > 0) return raw;
    if (typeof raw === 'string' && raw.trim()) {
      const n = Number(raw);
      if (Number.isFinite(n) && n > 0) return n;
    }
    if (raw && typeof raw === 'object' && 'definitionId' in raw) {
      const nested = parseDefinitionId((raw as { definitionId?: unknown }).definitionId);
      if (nested != null) return nested;
    }
  }
  return null;
}

function dayKey(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString().slice(0, 10);
}

function labelForDay(isoDay: string): string {
  const d = new Date(`${isoDay}T12:00:00Z`);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

const EformsSubmissionsTrend: React.FC<{
  embedded?: boolean;
  definitionId?: number | string;
  days?: number;
  chartType?: 'bar' | 'line';
  settings?: Record<string, unknown>;
}> = ({
  embedded,
  definitionId: definitionIdProp,
  days: daysProp = 14,
  chartType: chartTypeProp = 'bar',
  settings,
}) => {
  const definitionId = parseDefinitionId(definitionIdProp, settings);
  const days =
    typeof settings?.days === 'number' && settings.days > 0
      ? Math.min(60, Math.floor(settings.days))
      : daysProp;
  const chartType =
    settings?.chartType === 'line' || settings?.chartType === 'bar'
      ? settings.chartType
      : chartTypeProp;

  const { data, isLoading } = useEFormSubmissions(definitionId, undefined, 1, 200, {
    sortKey: 'createdAt',
    sortDirection: 'desc',
  });

  const series = useMemo(() => {
    const today = new Date();
    today.setHours(12, 0, 0, 0);
    const buckets = new Map<string, number>();
    for (let i = days - 1; i >= 0; i -= 1) {
      const d = new Date(today);
      d.setDate(today.getDate() - i);
      buckets.set(d.toISOString().slice(0, 10), 0);
    }
    for (const row of data ?? []) {
      const key = dayKey(row.submittedAt || row.createdAt);
      if (!key || !buckets.has(key)) continue;
      buckets.set(key, (buckets.get(key) ?? 0) + 1);
    }
    return Array.from(buckets.entries()).map(([key, value]) => ({
      label: labelForDay(key),
      value,
    }));
  }, [data, days]);

  const hasData = series.some((point) => point.value > 0);

  return (
    <WidgetShell
      title="E-Forms · Submissions trend"
      embedded={embedded}
      viewAllPath={definitionId ? `/e-forms/${definitionId}/submissions` : '/e-forms/submissions'}
      isLoading={isLoading}
      empty={!isLoading && (!definitionId || !hasData)}
      emptyMessage={
        definitionId
          ? `No submissions in the last ${days} days`
          : 'Choose a form to see the trend'
      }
    >
      {chartType === 'line' ? (
        <EFormLineChart data={series} height={180} />
      ) : (
        <EFormBarChart data={series} height={180} />
      )}
    </WidgetShell>
  );
};

export default EformsSubmissionsTrend;
